<script setup lang="ts">
/**
 * The contents page.
 *
 * Derived from the document each time it renders, never stored. A contents
 * page saved alongside the report is a contents page that goes wrong the first
 * time somebody renames a chapter, and nobody notices until it is printed.
 *
 * What is dynamic is what the headings say (their words, their numbers, their
 * pages, their colours); what is the charter's is how a level is set, which is
 * a named text style of the document chosen for each level in the inspector.
 * A chapter is set in its own colour, the palette's next one unless it was
 * given one, over whatever its style says: the colour is the chapter's, not
 * the style's.
 *
 * The leader dots are drawn with a repeating gradient rather than typed
 * periods, so they align to the same grid on every line whatever the title
 * length.
 */
import { useI18n } from 'vue-i18n'

import { computed } from 'vue'

import { contentsRows, type ContentsRow } from '@/domain/contents'
import { textStyleClassName } from '@/domain/layout/textStyles'
import type { ContentsSection } from '@/domain/section'
import { useReportStore } from '@/stores/report'

const { t } = useI18n()
const props = defineProps<{ section: ContentsSection }>()
const report = useReportStore()

const rows = computed(() =>
  contentsRows(report.sections, props.section.depth, {
    palette: report.layout.palette,
    numbers: report.headingNumbers,
  }),
)

/**
 * The leader dots, or nothing.
 *
 * A setting rather than a per-contents choice, because a report with two
 * contents pages set differently reads as two documents bound together.
 */
const leaders = computed(() => report.layout.contentsLeaders)

/** The text style chosen for the title, if any: the page's own heading otherwise. */
const titleClass = computed(() =>
  props.section.titleStyleId ? textStyleClassName(props.section.titleStyleId) : '',
)

/** The class of the text style chosen for this row's level, if any. */
function styleClass(row: ContentsRow): string {
  const id = props.section.levelStyleIds[Math.min(row.level, 3) - 1]
  return id ? textStyleClassName(id) : ''
}

/** What goes under a line: the pages it runs (below the first level), then the author's words. */
function detailOf(row: ContentsRow): string {
  if (!props.section.showDetail) return ''
  const pages =
    row.level > 1 && row.pages !== null
      ? t('structure.contentsPages', { count: row.pages }, row.pages)
      : ''
  return [pages, row.detail].filter(Boolean).join(' – ')
}
</script>

<template>
  <nav class="contents" :aria-label="t('sections.contents')">
    <h2 class="contents__title" :class="titleClass">{{ section.title }}</h2>
    <p v-if="section.subtitle" class="contents__subtitle">{{ section.subtitle }}</p>

    <p v-if="rows.length === 0" class="contents__empty">
      {{ t('canvas.contentsEmpty') }}
    </p>
    <ol v-else class="contents__list">
      <li
        v-for="row in rows"
        :key="row.id"
        class="contents__entry"
        :class="`contents__entry--${Math.min(row.level, 3)}`"
        :style="{ paddingLeft: `${(row.level - 1) * 6}mm` }"
      >
        <div class="contents__line">
          <span
            class="contents__label"
            :class="styleClass(row)"
            :style="row.colour ? { color: row.colour } : undefined"
          >
            <span v-if="row.number" class="contents__number">{{ row.number }}</span>
            {{ row.label }}
          </span>
          <span v-if="leaders" class="contents__leader" aria-hidden="true" />
          <span v-if="section.pageNumbers && row.page !== null" class="contents__page">
            {{ t('structure.contentsPage', { page: row.page }) }}
          </span>
        </div>
        <p v-if="detailOf(row)" class="contents__detail">{{ detailOf(row) }}</p>
      </li>
    </ol>
  </nav>
</template>

<style scoped>
.contents__title {
  margin-top: 0;
}

.contents__subtitle {
  margin: -2mm 0 8mm;
  color: var(--paper-ink-soft);
}

.contents__empty {
  color: var(--paper-ink-soft);
  font-style: italic;
}

.contents__list {
  margin: 0;
  padding: 0;
  list-style: none;
}

.contents__entry {
  margin-bottom: 3mm;
}

/* A chapter opens a block: air above it, and its line set larger than the rest. */
.contents__entry--1 {
  margin-top: 9mm;
  margin-bottom: 3mm;
  font-size: 1.15em;
  font-weight: 700;
}

.contents__line {
  display: flex;
  align-items: baseline;
  gap: 2mm;
}

.contents__label {
  flex: 0 1 auto;
  max-width: 82%;
}

/* Numbers in a column of their own width, so the titles line up under one
   another instead of stepping in and out with the length of each number. */
.contents__number {
  display: inline-block;
  min-width: 9mm;
  color: inherit;
}

/* The page is pushed to the right edge when there are no leader dots to push it. */
.contents__page {
  flex: none;
  margin-left: auto;
  color: var(--page-accent);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.contents__leader {
  flex: 1;
  height: 1px;
  transform: translateY(-2px);
  background-image: radial-gradient(circle, var(--paper-ink-soft) 0.6px, transparent 0.7px);
  background-size: 3.5px 1px;
  background-repeat: repeat-x;
  opacity: 0.65;
}

.contents__detail {
  margin: 0.6mm 0 0;
  color: var(--paper-ink-soft);
  font-size: 0.82em;
  font-weight: 400;
  text-transform: none;
}
</style>
